import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { requireData } from "../../shared/api/api-error.js";
import { useApi } from "../../shared/api/api-context.js";
import type { components } from "../../shared/api/generated/trading-engine.js";
import {
  liveChartPeriod,
  loadBacktestPeriod,
  loadMarketPeriod,
  type ChartPeriod,
  type PeriodProgress,
} from "./full-period-data.js";

export function useBacktestChart(backtestId: string, enabled = true, range: ChartPeriod) {
  const api = useApi();
  const [progress, setProgress] = useState<PeriodProgress>();
  const query = useQuery({
    enabled: enabled && backtestId !== "",
    queryKey: ["backtests", backtestId, "chart", "full-period", range.from, range.to],
    retry: false,
    queryFn: async ({ signal }) =>
      loadBacktestPeriod(
        backtestId,
        range,
        async (cursor) =>
          requireData(
            await api.GET("/backtests/{backtestId}/chart", {
              params: {
                path: { backtestId },
                query: { ...range, ...(cursor === undefined ? {} : { cursor }) },
              },
              signal,
            }),
          ),
        signal,
        setProgress,
      ),
    staleTime: Number.POSITIVE_INFINITY,
  });
  return { ...query, progress };
}

export function useTickerBars(
  symbol: string,
  interval: "1m" | "5m" | "15m" | "1h" | "1d",
  enabled = true,
  firstExecutionAt?: string,
) {
  const api = useApi();
  const history = useRef({ symbol, pages: new Map<string, components["schemas"]["TickerBars"]>() });
  const [progress, setProgress] = useState<PeriodProgress>();
  const query = useQuery({
    enabled: enabled && symbol !== "",
    queryKey: ["tickers", symbol, "bars", "full-period", interval, firstExecutionAt],
    retry: false,
    queryFn: async ({ signal }) => {
      if (history.current.symbol !== symbol) history.current = { symbol, pages: new Map() };
      const historicalPages = history.current.pages;
      const period = liveChartPeriod(
        firstExecutionAt === undefined ? [] : [{ occurredAt: firstExecutionAt }],
        interval,
      );
      return loadMarketPeriod(
        symbol,
        interval,
        period,
        (range, historical) => {
          const fetch = async (requestSignal: AbortSignal) =>
            requireData(
              await api.GET("/tickers/{symbol}/bars", {
                params: { path: { symbol }, query: { adjustment: "raw", ...range, interval } },
                signal: requestSignal,
              }),
            );
          // Only the newest window is refreshed every minute; completed windows
          // can be reused across full-range refreshes and interval/view navigation.
          if (!historical) return fetch(signal);
          const key = JSON.stringify([interval, range.from, range.to]);
          const cached = historicalPages.get(key);
          if (cached !== undefined) return Promise.resolve(cached);
          return fetch(signal).then((page) => {
            signal.throwIfAborted();
            historicalPages.set(key, page);
            return page;
          });
        },
        signal,
        setProgress,
      );
    },
    refetchInterval: 60_000,
  });
  return { ...query, progress };
}
